import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamProposal } from "@/lib/stream";
import { FORM, BANKING } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Sparkles, Landmark, Plus, Trash2 } from "lucide-react";
import RegionContextCard from "@/components/RegionContextCard";

const emptyLoan = {
  business_name: "",
  industry_id: "construction",
  region_id: "",
  loan_amount: "",
  currency_code: "USD",
  tenure_months: 36,
  purpose: "",
  annual_revenue: "",
  years_in_operation: 2,
  use_of_funds: "",
  repayment_strategy: "",
  contact_name: "",
  contact_email: "",
  country: "",
};

const emptyBanking = {
  bank_name: "",
  account_holder: "",
  primary_identifier: "",
  swift_bic: "",
  branch_code: "",
  notes: "",
};

const emptyGrant = {
  business_name: "",
  project_title: "",
  grant_amount: "",
  currency_code: "USD",
  region_id: "",
  funder_name: "",
  project_summary: "",
  problem_statement: "",
  objectives: "",
  target_beneficiaries: "",
  country: "",
};

export default function ProposalForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialType = params.get("type") === "grant" ? "grant" : "loan";
  const [type, setType] = useState(initialType);

  const [industries, setIndustries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loan, setLoan] = useState({
    ...emptyLoan,
    industry_id: params.get("industry") || emptyLoan.industry_id,
    currency_code: params.get("currency") || emptyLoan.currency_code,
    region_id: params.get("region") || "",
  });
  const [grant, setGrant] = useState({
    ...emptyGrant,
    currency_code: params.get("currency") || emptyGrant.currency_code,
    region_id: params.get("region") || "",
  });
  const [banking, setBanking] = useState(emptyBanking);
  const [includeBanking, setIncludeBanking] = useState(false);
  const [collateral, setCollateral] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [iRes, cRes, rRes] = await Promise.all([
        api.get("/industries"),
        api.get("/currencies"),
        api.get("/regions"),
      ]);
      setIndustries(iRes.data.industries);
      setCurrencies(cRes.data.currencies);
      setRegions(rRes.data.regions);

      // If a region was passed via URL and no explicit currency, snap to region default.
      const initialRegion = params.get("region");
      const initialCurrency = params.get("currency");
      if (initialRegion && !initialCurrency) {
        const r = rRes.data.regions.find((x) => x.id === initialRegion);
        if (r?.default_currency) {
          setLoan((s) => ({ ...s, currency_code: r.default_currency }));
          setGrant((s) => ({ ...s, currency_code: r.default_currency }));
        }
      }
    })();
  }, []);

  const selectedIndustry = useMemo(
    () => industries.find((i) => i.id === loan.industry_id),
    [industries, loan.industry_id]
  );
  const selectedRegion = useMemo(
    () =>
      regions.find(
        (r) => r.id === (type === "loan" ? loan.region_id : grant.region_id)
      ) || null,
    [regions, type, loan.region_id, grant.region_id]
  );

  const identifierLabel =
    selectedRegion?.primary_identifier_label ||
    "Account number / IBAN / routing";

  const updateLoan = (patch) => setLoan((s) => ({ ...s, ...patch }));
  const updateGrant = (patch) => setGrant((s) => ({ ...s, ...patch }));
  const updateBanking = (patch) => setBanking((s) => ({ ...s, ...patch }));

  const applyRegion = (regionId, target) => {
    const r = regions.find((x) => x.id === regionId);
    const patch = { region_id: regionId };
    // Snap currency to region default (only if the user hasn't manually typed
    // a currency this session — we just always update because the region
    // implies a jurisdiction, and the user can override afterwards).
    if (r?.default_currency) patch.currency_code = r.default_currency;
    if (target === "loan") updateLoan(patch);
    else updateGrant(patch);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (type === "loan") {
        const payload = {
          ...loan,
          loan_amount: Number(loan.loan_amount),
          annual_revenue: Number(loan.annual_revenue),
          tenure_months: Number(loan.tenure_months),
          years_in_operation: Number(loan.years_in_operation),
        };
        if (!payload.region_id) delete payload.region_id;
        if (includeBanking) {
          const hasAny = Object.values(banking).some((v) => v && String(v).trim());
          if (hasAny) payload.banking_details = banking;
        }
        // Only include collateral items that have both a description and a
        // positive numeric value. Everything else is silently dropped so a
        // half-filled row never blocks submission.
        const validCollateral = collateral
          .filter((c) => c.description?.trim() && Number(c.estimated_value) > 0)
          .map(({ _id, ...c }) => ({
            ...c,
            estimated_value: Number(c.estimated_value),
            currency_code: c.currency_code || payload.currency_code,
          }));
        if (validCollateral.length) payload.collateral = validCollateral;
        if (!payload.business_name || !payload.loan_amount || !payload.purpose) {
          toast.error("Please fill in business name, amount and purpose.");
          setSubmitting(false);
          return;
        }
        // Persist a "streaming" placeholder so preview can pick up immediately
        sessionStorage.setItem(
          "imali:draft",
          JSON.stringify({
            type: "loan",
            title: "Drafting your loan proposal…",
            industry_id: payload.industry_id,
            region_id: payload.region_id || null,
            business_name: payload.business_name,
            amount: payload.loan_amount,
            currency_code: payload.currency_code,
            content: "",
            streaming: true,
          })
        );
        navigate("/proposals/draft/preview");
        // Kick off the stream — the preview page reads updates from sessionStorage
        streamProposal(
          "/generate/loan-proposal/stream",
          payload,
          {
            onMeta: (meta) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              const next = { ...draft, ...meta, streaming: true };
              sessionStorage.setItem("imali:draft", JSON.stringify(next));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: next }));
            },
            onToken: (chunk) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              draft.content = (draft.content || "") + chunk;
              sessionStorage.setItem("imali:draft", JSON.stringify(draft));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: draft }));
            },
            onDone: (finalContent) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              draft.content = finalContent;
              draft.streaming = false;
              sessionStorage.setItem("imali:draft", JSON.stringify(draft));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: draft }));
            },
          }
        ).catch((err) => {
          toast.error(err?.message || "Streaming failed");
          window.dispatchEvent(new CustomEvent("imali:draft-error", { detail: err?.message }));
        });
      } else {
        const payload = { ...grant, grant_amount: Number(grant.grant_amount) };
        if (!payload.region_id) delete payload.region_id;
        if (!payload.business_name || !payload.project_title || !payload.grant_amount) {
          toast.error("Please fill in organization, project title and amount.");
          setSubmitting(false);
          return;
        }
        sessionStorage.setItem(
          "imali:draft",
          JSON.stringify({
            type: "grant",
            title: "Drafting your grant proposal…",
            region_id: payload.region_id || null,
            business_name: payload.business_name,
            amount: payload.grant_amount,
            currency_code: payload.currency_code,
            content: "",
            streaming: true,
          })
        );
        navigate("/proposals/draft/preview");
        streamProposal(
          "/generate/grant-proposal/stream",
          payload,
          {
            onMeta: (meta) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              const next = { ...draft, ...meta, streaming: true };
              sessionStorage.setItem("imali:draft", JSON.stringify(next));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: next }));
            },
            onToken: (chunk) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              draft.content = (draft.content || "") + chunk;
              sessionStorage.setItem("imali:draft", JSON.stringify(draft));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: draft }));
            },
            onDone: (finalContent) => {
              const draft = JSON.parse(sessionStorage.getItem("imali:draft") || "{}");
              draft.content = finalContent;
              draft.streaming = false;
              sessionStorage.setItem("imali:draft", JSON.stringify(draft));
              window.dispatchEvent(new CustomEvent("imali:draft-update", { detail: draft }));
            },
          }
        ).catch((err) => {
          toast.error(err?.message || "Streaming failed");
          window.dispatchEvent(new CustomEvent("imali:draft-error", { detail: err?.message }));
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Generation failed";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid={FORM.root}>
      <div className="max-w-3xl">
        <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-4">
          New proposal
        </p>
        <h1 className="font-display text-4xl lg:text-5xl leading-none tracking-tighter font-light">
          Let Imali draft your{" "}
          <span className="text-primary">
            {type === "loan" ? "business loan proposal" : "grant proposal"}
          </span>
          .
        </h1>
        <p className="mt-4 text-muted-foreground">
          Provide the details below. Imali generates a structured, professional
          proposal you can copy, download as PDF or DOCX, and save.
        </p>
      </div>

      {/* Type toggle */}
      <div className="mt-8 inline-flex bg-muted rounded-full p-1 border border-border">
        <button
          type="button"
          data-testid={FORM.typeToggleLoan}
          onClick={() => setType("loan")}
          className={`px-5 h-10 rounded-full text-sm font-medium transition-colors ${
            type === "loan"
              ? "bg-primary text-primary-foreground"
              : "text-foreground/70 hover:text-foreground"
          }`}
        >
          Business Loan
        </button>
        <button
          type="button"
          data-testid={FORM.typeToggleGrant}
          onClick={() => setType("grant")}
          className={`px-5 h-10 rounded-full text-sm font-medium transition-colors ${
            type === "grant"
              ? "bg-primary text-primary-foreground"
              : "text-foreground/70 hover:text-foreground"
          }`}
        >
          Grant
        </button>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-12">
        {type === "loan" ? (
          <>
            <Section
              step="01"
              title="Business information"
              hint="Who is applying and where do they operate?"
            >
              <Field label="Business name">
                <Input
                  data-testid={FORM.businessName}
                  value={loan.business_name}
                  onChange={(e) => updateLoan({ business_name: e.target.value })}
                  placeholder="e.g. Baobab Contractors Ltd"
                  required
                />
              </Field>
              <Field label="Industry">
                <Select
                  value={loan.industry_id}
                  onValueChange={(v) => updateLoan({ industry_id: v })}
                >
                  <SelectTrigger data-testid={FORM.industrySelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Region / banking system">
                <Select
                  value={loan.region_id || "__none"}
                  onValueChange={(v) =>
                    applyRegion(v === "__none" ? "" : v, "loan")
                  }
                >
                  <SelectTrigger data-testid={FORM.regionSelect}>
                    <SelectValue placeholder="Select a region (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No region — international</SelectItem>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Country (optional)">
                <Input
                  data-testid={FORM.country}
                  value={loan.country}
                  onChange={(e) => updateLoan({ country: e.target.value })}
                  placeholder="e.g. South Africa"
                />
              </Field>
              <Field label="Years in operation">
                <Input
                  data-testid={FORM.yearsInOp}
                  type="number"
                  min="0"
                  value={loan.years_in_operation}
                  onChange={(e) => updateLoan({ years_in_operation: e.target.value })}
                />
              </Field>
            </Section>

            <Section
              step="02"
              title="Loan request & financials"
              hint={
                selectedIndustry
                  ? `Envelope: ${selectedIndustry.min_amount.toLocaleString()}–${selectedIndustry.max_amount.toLocaleString()} USD · ${selectedIndustry.min_tenure_months}–${selectedIndustry.max_tenure_months} months`
                  : ""
              }
            >
              <Field label="Loan amount">
                <Input
                  data-testid={FORM.amount}
                  type="number"
                  min="0"
                  value={loan.loan_amount}
                  onChange={(e) => updateLoan({ loan_amount: e.target.value })}
                  placeholder="250000"
                  required
                />
              </Field>
              <Field label="Currency">
                <Select
                  value={loan.currency_code}
                  onValueChange={(v) => updateLoan({ currency_code: v })}
                >
                  <SelectTrigger data-testid={FORM.currencySelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="mr-2">{c.flag}</span>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tenure (months)">
                <Input
                  data-testid={FORM.tenure}
                  type="number"
                  min="1"
                  value={loan.tenure_months}
                  onChange={(e) => updateLoan({ tenure_months: e.target.value })}
                />
              </Field>
              <Field label="Annual revenue">
                <Input
                  data-testid={FORM.annualRevenue}
                  type="number"
                  min="0"
                  value={loan.annual_revenue}
                  onChange={(e) => updateLoan({ annual_revenue: e.target.value })}
                  placeholder="1500000"
                />
              </Field>
            </Section>

            <Section
              step="03"
              title="Purpose & repayment"
              hint="What is the money for and how will you repay?"
            >
              <Field label="Purpose (short)" full>
                <Input
                  data-testid={FORM.purpose}
                  value={loan.purpose}
                  onChange={(e) => updateLoan({ purpose: e.target.value })}
                  placeholder="e.g. Purchase 2 excavators for infrastructure project"
                  required
                />
              </Field>
              <Field label="Use of funds (detailed)" full>
                <Textarea
                  data-testid={FORM.useOfFunds}
                  rows={4}
                  value={loan.use_of_funds}
                  onChange={(e) => updateLoan({ use_of_funds: e.target.value })}
                  placeholder="Break down the loan by category (equipment, working capital, hires, etc.)"
                />
              </Field>
              <Field label="Repayment strategy" full>
                <Textarea
                  data-testid={FORM.repaymentStrategy}
                  rows={3}
                  value={loan.repayment_strategy}
                  onChange={(e) => updateLoan({ repayment_strategy: e.target.value })}
                  placeholder="e.g. Repay from monthly project milestone billing, DSCR ≥ 1.4x"
                />
              </Field>
              <Field label="Contact name">
                <Input
                  data-testid={FORM.contactName}
                  value={loan.contact_name}
                  onChange={(e) => updateLoan({ contact_name: e.target.value })}
                />
              </Field>
              <Field label="Contact email">
                <Input
                  data-testid={FORM.contactEmail}
                  type="email"
                  value={loan.contact_email}
                  onChange={(e) => updateLoan({ contact_email: e.target.value })}
                />
              </Field>
            </Section>

            {/* Regional banking context (shown when a region is selected) */}
            {selectedRegion && (
              <div>
                <RegionContextCard region={selectedRegion} dense />
              </div>
            )}

            {/* Banking & Disbursement (optional) */}
            <section data-testid={BANKING.section}>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs tracking-[0.28em] text-secondary">
                    04
                  </span>
                  <h2 className="font-display text-2xl tracking-tight">
                    Banking &amp; disbursement
                  </h2>
                  <span className="hidden md:inline text-xs text-muted-foreground">
                    Optional — appears as a dedicated section in the proposal
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {includeBanking ? "Included" : "Not included"}
                  </span>
                  <Switch
                    data-testid={BANKING.toggle}
                    checked={includeBanking}
                    onCheckedChange={setIncludeBanking}
                  />
                </div>
              </div>

              {includeBanking && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  <Field label="Bank name">
                    <Input
                      data-testid={BANKING.bankName}
                      value={banking.bank_name}
                      onChange={(e) => updateBanking({ bank_name: e.target.value })}
                      placeholder="e.g. Standard Bank / HSBC / SBI"
                    />
                  </Field>
                  <Field label="Account holder">
                    <Input
                      data-testid={BANKING.accountHolder}
                      value={banking.account_holder}
                      onChange={(e) =>
                        updateBanking({ account_holder: e.target.value })
                      }
                    />
                  </Field>
                  <Field label={identifierLabel}>
                    <Input
                      data-testid={BANKING.identifier}
                      value={banking.primary_identifier}
                      onChange={(e) =>
                        updateBanking({ primary_identifier: e.target.value })
                      }
                      placeholder={
                        selectedRegion
                          ? `Enter ${identifierLabel.split(" ")[0]}`
                          : "IBAN / NUBAN / ABA + acct / IFSC + acct"
                      }
                    />
                  </Field>
                  <Field label="SWIFT / BIC (cross-border)">
                    <Input
                      data-testid={BANKING.swift}
                      value={banking.swift_bic}
                      onChange={(e) => updateBanking({ swift_bic: e.target.value })}
                      placeholder="e.g. SBZAZAJJ"
                    />
                  </Field>
                  <Field label="Branch / sort code (if applicable)">
                    <Input
                      data-testid={BANKING.branchCode}
                      value={banking.branch_code}
                      onChange={(e) =>
                        updateBanking({ branch_code: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Disbursement notes">
                    <Input
                      data-testid={BANKING.notes}
                      value={banking.notes}
                      onChange={(e) => updateBanking({ notes: e.target.value })}
                      placeholder="e.g. tranche schedule, escrow, currency conversion"
                    />
                  </Field>
                  {!selectedRegion && (
                    <p className="md:col-span-2 text-xs text-muted-foreground">
                      Tip: Select a region above and the identifier field will
                      relabel automatically (IBAN, IFSC, NUBAN, ABA routing, BSB,
                      etc.).
                    </p>
                  )}
                </div>
              )}
            </section>

            <CollateralSection
              collateral={collateral}
              setCollateral={setCollateral}
              loanCurrency={loan.currency_code}
              loanAmount={Number(loan.loan_amount) || 0}
              currencies={currencies}
              onSuggestLoanAmount={(amt) => updateLoan({ loan_amount: amt })}
            />
          </>
        ) : (
          <>
            <Section step="01" title="Organization & project">
              <Field label="Organization name">
                <Input
                  data-testid={FORM.businessName}
                  value={grant.business_name}
                  onChange={(e) => updateGrant({ business_name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Project title">
                <Input
                  data-testid={FORM.projectTitle}
                  value={grant.project_title}
                  onChange={(e) => updateGrant({ project_title: e.target.value })}
                  required
                />
              </Field>
              <Field label="Funder name (optional)">
                <Input
                  data-testid={FORM.funderName}
                  value={grant.funder_name}
                  onChange={(e) => updateGrant({ funder_name: e.target.value })}
                  placeholder="e.g. Gates Foundation"
                />
              </Field>
              <Field label="Country">
                <Input
                  data-testid={FORM.country}
                  value={grant.country}
                  onChange={(e) => updateGrant({ country: e.target.value })}
                />
              </Field>
              <Field label="Region / banking system">
                <Select
                  value={grant.region_id || "__none"}
                  onValueChange={(v) =>
                    applyRegion(v === "__none" ? "" : v, "grant")
                  }
                >
                  <SelectTrigger data-testid={FORM.regionSelect}>
                    <SelectValue placeholder="Select a region (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No region — international</SelectItem>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Grant amount">
                <Input
                  data-testid={FORM.amount}
                  type="number"
                  min="0"
                  value={grant.grant_amount}
                  onChange={(e) => updateGrant({ grant_amount: e.target.value })}
                  required
                />
              </Field>
              <Field label="Currency">
                <Select
                  value={grant.currency_code}
                  onValueChange={(v) => updateGrant({ currency_code: v })}
                >
                  <SelectTrigger data-testid={FORM.currencySelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="mr-2">{c.flag}</span>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            <Section step="02" title="Project narrative">
              <Field label="Project summary" full>
                <Textarea
                  data-testid={FORM.projectSummary}
                  rows={3}
                  value={grant.project_summary}
                  onChange={(e) => updateGrant({ project_summary: e.target.value })}
                />
              </Field>
              <Field label="Problem statement" full>
                <Textarea
                  data-testid={FORM.problemStatement}
                  rows={3}
                  value={grant.problem_statement}
                  onChange={(e) => updateGrant({ problem_statement: e.target.value })}
                />
              </Field>
              <Field label="Objectives" full>
                <Textarea
                  data-testid={FORM.objectives}
                  rows={3}
                  value={grant.objectives}
                  onChange={(e) => updateGrant({ objectives: e.target.value })}
                />
              </Field>
              <Field label="Target beneficiaries" full>
                <Textarea
                  data-testid={FORM.beneficiaries}
                  rows={2}
                  value={grant.target_beneficiaries}
                  onChange={(e) =>
                    updateGrant({ target_beneficiaries: e.target.value })
                  }
                />
              </Field>
            </Section>

            {selectedRegion && (
              <div>
                <RegionContextCard region={selectedRegion} dense />
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-end gap-4 border-t border-border pt-6">
          <Button
            data-testid={FORM.submit}
            type="submit"
            size="lg"
            disabled={submitting}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8"
          >
            {submitting ? (
              <>
                <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                Drafting proposal…
              </>
            ) : (
              <>
                <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" />
                Generate proposal
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ step, title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-4 border-b border-border pb-3">
        <span className="font-mono text-xs tracking-[0.28em] text-secondary">
          {step}
        </span>
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        {hint ? (
          <span className="hidden md:block text-xs text-muted-foreground ml-auto">
            {hint}
          </span>
        ) : null}
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {children}
      </div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const COLLATERAL_KINDS = [
  { value: "property", label: "Real estate / property" },
  { value: "equipment", label: "Equipment / machinery" },
  { value: "inventory", label: "Inventory / stock" },
  { value: "vehicle", label: "Vehicle" },
  { value: "receivables", label: "Trade receivables" },
  { value: "cash_deposit", label: "Cash / fixed deposit" },
  { value: "personal_guarantee", label: "Personal guarantee" },
  { value: "livestock", label: "Livestock" },
  { value: "standing_crops", label: "Standing crops / harvest lien" },
  { value: "other", label: "Other" },
];

const OWNERSHIP_KINDS = ["sole", "joint", "leased", "financed"];

function emptyCollateralRow(defaultCurrency = "USD") {
  return {
    _id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: "property",
    description: "",
    estimated_value: "",
    currency_code: defaultCurrency,
    ownership: "sole",
    encumbered: false,
    notes: "",
  };
}

function CollateralSection({
  collateral, setCollateral, loanCurrency, loanAmount, currencies,
  onSuggestLoanAmount,
}) {
  const addRow = () =>
    setCollateral((rows) => [...rows, emptyCollateralRow(loanCurrency)]);
  const removeRow = (idx) =>
    setCollateral((rows) => rows.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) =>
    setCollateral((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );

  // Compute LTV over items whose currency matches the loan currency.
  const sameCcyTotal = collateral
    .filter((c) => (c.currency_code || loanCurrency) === loanCurrency)
    .reduce((sum, c) => sum + (Number(c.estimated_value) || 0), 0);
  const ltvPct = loanAmount > 0 && sameCcyTotal > 0
    ? Math.round((loanAmount / sameCcyTotal) * 1000) / 10
    : null;
  const ltvBadge = (() => {
    if (ltvPct == null) return null;
    if (ltvPct <= 80)
      return { tone: "border-primary/50 text-primary bg-primary/[0.08]", label: "Well-collateralised" };
    if (ltvPct <= 100)
      return { tone: "border-secondary/50 text-secondary bg-secondary/[0.08]", label: "Adequate" };
    return { tone: "border-destructive/50 text-destructive bg-destructive/[0.08]", label: "UNDER-collateralised" };
  })();

  // When under-collateralised, suggest a loan amount at 80% LTV (Well bucket).
  const suggestedLoan = ltvPct != null && ltvPct > 100
    ? Math.floor(sameCcyTotal * 0.8)
    : null;
  const suggestedFmt = suggestedLoan
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(suggestedLoan)
    : null;

  return (
    <section data-testid="form-collateral-section">
      <div className="flex items-baseline gap-4 border-b border-border pb-3">
        <span className="font-mono text-xs tracking-[0.28em] text-secondary">
          {/* Follows the last section step in the loan form (banking = 05) */}
          06
        </span>
        <h2 className="font-display text-2xl tracking-tight">
          Collateral offered
          <span className="ml-2 font-mono text-xs text-muted-foreground">
            (optional)
          </span>
        </h2>
        {ltvBadge && (
          <span
            data-testid="form-collateral-ltv"
            className={`ml-auto inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-mono ${ltvBadge.tone}`}
          >
            LTV {ltvPct}% · {ltvBadge.label}
          </span>
        )}
        {suggestedLoan && (
          <button
            data-testid="form-collateral-suggest-loan"
            type="button"
            onClick={() => onSuggestLoanAmount?.(suggestedLoan)}
            title="Sets your loan amount to 80% of same-currency collateral — lands you in the Well-collateralised bucket."
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/50 bg-primary/[0.08] text-primary px-3 py-1 text-xs font-mono hover:bg-primary/[0.14] transition-colors"
          >
            <Sparkles aria-hidden="true" className="w-3 h-3" />
            Try {loanCurrency} {suggestedFmt}
          </button>
        )}
      </div>

      {collateral.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No collateral added — the loan will be treated as
            unsecured / guarantee-only. Add items below to strengthen the
            proposal and let Imali compute your Loan-to-Value coverage.
          </p>
          <Button
            data-testid="form-collateral-add-first"
            type="button"
            variant="outline"
            className="mt-4 rounded-full"
            onClick={addRow}
          >
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            Add first collateral item
          </Button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {collateral.map((row, idx) => (
            <div
              key={row._id || idx}
              data-testid={`form-collateral-row-${idx}`}
              className="rounded-2xl border border-border bg-card p-5 grid grid-cols-1 md:grid-cols-12 gap-3"
            >
              <div className="md:col-span-3">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Kind
                </Label>
                <Select
                  value={row.kind}
                  onValueChange={(v) => updateRow(idx, { kind: v })}
                >
                  <SelectTrigger data-testid={`form-collateral-kind-${idx}`} className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLATERAL_KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-5">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Description
                </Label>
                <Input
                  data-testid={`form-collateral-description-${idx}`}
                  className="mt-1"
                  value={row.description}
                  onChange={(e) => updateRow(idx, { description: e.target.value })}
                  placeholder="e.g. 20-acre farm plot title deed LR/12345"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Est. value
                </Label>
                <Input
                  data-testid={`form-collateral-value-${idx}`}
                  className="mt-1"
                  type="number"
                  min="0"
                  value={row.estimated_value}
                  onChange={(e) => updateRow(idx, { estimated_value: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Currency
                </Label>
                <Select
                  value={row.currency_code || loanCurrency}
                  onValueChange={(v) => updateRow(idx, { currency_code: v })}
                >
                  <SelectTrigger data-testid={`form-collateral-currency-${idx}`} className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-3">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Ownership
                </Label>
                <Select
                  value={row.ownership}
                  onValueChange={(v) => updateRow(idx, { ownership: v })}
                >
                  <SelectTrigger data-testid={`form-collateral-ownership-${idx}`} className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OWNERSHIP_KINDS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="md:col-span-3 flex items-center gap-2 text-xs mt-6">
                <input
                  data-testid={`form-collateral-encumbered-${idx}`}
                  type="checkbox"
                  checked={!!row.encumbered}
                  onChange={(e) => updateRow(idx, { encumbered: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-foreground/85">
                  Already pledged elsewhere
                </span>
              </label>
              <div className="md:col-span-5">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Notes (optional)
                </Label>
                <Input
                  data-testid={`form-collateral-notes-${idx}`}
                  className="mt-1"
                  value={row.notes || ""}
                  onChange={(e) => updateRow(idx, { notes: e.target.value })}
                  placeholder="Escrow arrangement, insurance status, valuation date, ..."
                />
              </div>
              <div className="md:col-span-12 flex justify-end">
                <Button
                  data-testid={`form-collateral-remove-${idx}`}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-destructive hover:text-destructive"
                  onClick={() => removeRow(idx)}
                >
                  <Trash2 aria-hidden="true" className="w-4 h-4 mr-1.5" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button
            data-testid="form-collateral-add"
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={addRow}
          >
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            Add another item
          </Button>
        </div>
      )}
    </section>
  );
}
